import {
  Component,
  OnInit,
  Input,
  Output,
  Renderer2,
  EventEmitter,
} from "@angular/core";
import {
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
} from "@angular/forms";
import { ModalManagementService } from "@app/shared/utils/modal-management.service";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { IResponseMinimalCard } from "@interfaces/i-response-card-view-model";
import { LoadingOverlayService } from "@app/shared/services/loading-overlay.service";
import { WalletV3Service } from "@services/wallet-v3.service";

enum EditResult {
  Default = "default",
  Success = "success",
  Error = "error",
}
@Component({
  selector: "app-edit-user-card-modal",
  templateUrl: "./edit-user-card-modal.component.html",
  styleUrls: ["./edit-user-card-modal.component.scss"],
})
export class EditUserCardModalComponent implements OnInit {
  @Input() userCard: IResponseMinimalCard;
  @Output() onEditedCard: EventEmitter<IResponseMinimalCard[]> =
    new EventEmitter();
  editResult = EditResult;
  form: FormGroup;
  result: EditResult = EditResult.Default;

  constructor(
    private activeModal: NgbActiveModal,
    private modalManagementService: ModalManagementService,
    private loadingOverlayService: LoadingOverlayService,
    private walletService: WalletV3Service,
    private renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.setupInputFormatting();
  }

  private buildForm(): void {
    this.form = new FormGroup({
      number: new FormControl("", [
        Validators.required,
        this.cardEndsWithValidator.bind(this),
      ]),
      expDate: new FormControl("", [
        Validators.required,
        this.expDateValidator.bind(this),
      ]),
      cvv: new FormControl("", [Validators.required]),
    });
  }

  private setupInputFormatting(): void {
    this.form.get("number")?.valueChanges.subscribe((value) => {
      if (value !== null && value !== undefined) {
        const cleaned = value.replace(/\D+/g, "");
        const limited = cleaned.slice(0, 16);
        const chunks = limited.match(/.{1,4}/g) || [];
        const formatted = chunks.join("-");

        if (value !== formatted) {
          this.form.get("number")?.setValue(formatted, { emitEvent: false });
        }
      }
    });

    this.form.get("expDate")?.valueChanges.subscribe((value) => {
      if (value !== null && value !== undefined) {
        const cleaned = value.replace(/\D+/g, "");
        const limited = cleaned.slice(0, 6);

        let formatted = "";
        if (limited.length >= 2) {
          formatted = limited.slice(0, 2);
          if (limited.length > 2) {
            formatted += "/" + limited.slice(2);
          }
        } else {
          formatted = limited;
        }

        if (value !== formatted) {
          this.form.get("expDate")?.setValue(formatted, { emitEvent: false });
        }
      }
    });

    this.form.get("cvv")?.valueChanges.subscribe((value) => {
      if (value !== null && value !== undefined) {
        const cleaned = value.replace(/\D+/g, "");
        const limited = cleaned.slice(0, 4);

        if (value !== limited) {
          this.form.get("cvv")?.setValue(limited, { emitEvent: false });
        }
      }
    });
  }

  private cardEndsWithValidator(
    control: AbstractControl,
  ): { [key: string]: any } | null {
    const value: string = control.value?.replace(/\D+/g, "") ?? "";

    if (value.length < 4) {
      return null;
    }

    const last4 = value.slice(-4);
    const expectedLast4 = this.userCard?.last4;

    if (last4 !== expectedLast4) {
      return { invalidEnding: true };
    }

    return null;
  }

  private expDateValidator(
    control: AbstractControl,
  ): { [key: string]: any } | null {
    const value: string = control.value;

    if (!value || value.length < 4) {
      return { incomplete: true };
    }

    const [monthStr, yearStr] = value.split("/");

    const month = parseInt(monthStr, 10);
    let year = parseInt(yearStr, 10);

    if (isNaN(month) || month < 1 || month > 12) {
      return { invalidDate: true };
    }

    if (isNaN(year)) {
      return { invalidDate: true };
    }

    if (yearStr.length === 2) {
      const currentYear = new Date().getFullYear();
      const prefix = Math.floor(currentYear / 100) * 100;
      year = prefix + year;
    }

    const currentYear = new Date().getFullYear();
    const maxYear = currentYear + 50;

    if (year > maxYear) {
      return { yearOutOfRange: true };
    }

    const now = new Date();
    const currentMonth = now.getMonth() + 1;

    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      return { expired: true };
    }

    return null;
  }

  public updateCreditCard(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      return;
    } else {
      this.loadingOverlayService.showLoadingOverlay();

      const number = this.form.get("number")?.value.replace(/-/g, "");
      const cvv = this.form.get("cvv")?.value;
      const expDate = this.form.get("expDate")?.value;

      const [monthStr, yearStr] = expDate.split("/");
      const expiryMonth = parseInt(monthStr, 10);
      const expiryYear = parseInt(yearStr, 10);

      const payload = {
        cardId: this.userCard.cardId,
        cardNumber: number,
        expiryYear: expiryYear,
        expiryMonth: expiryMonth,
        cvv: cvv,
      };

      this.walletService.editUserCreditCard(payload).subscribe((success) => {
        if (success) {
          this.walletService
            .getUserCreditCardsMinimal()
            .subscribe((res: IResponseMinimalCard[]) => {
              this.onEditedCard.emit(res);

              this.loadingOverlayService.hideLoadingOverlay();
              this.showSuccessMessage();
            });
        } else {
          this.loadingOverlayService.hideLoadingOverlay();
          this.showErrorMessage();
        }
      });
    }
  }

  showSuccessMessage() {
    this.loadingOverlayService.hideLoadingOverlay();
    this.result = this.editResult.Success;
    this.removeElementClass(".modal-edit-user-card", "step-one");
  }

  showErrorMessage() {
    this.loadingOverlayService.hideLoadingOverlay();
    this.result = this.editResult.Error;
    this.removeElementClass(".modal-edit-user-card", "step-one");
  }

  removeElementClass(findElement: string, typeClass: string): void {
    const element = document.querySelector(findElement);
    if (element) {
      this.renderer.removeClass(element, typeClass);
    }
  }

  public closeModal(): void {
    this.modalManagementService.executeAnimateBottom(
      this.activeModal,
      true,
      null,
      ".modal-edit-user-card",
    );
  }
}
